// Primary action button — the single most important action in a page
// or modal. Use exactly one per context (per UI_GUIDELINES.md §3.1).
//
// Visual: filled brand blue (#2563eb via bg-blue-600), white text.
// States:
//   - default   — bg-blue-600 white
//   - hover     — bg-blue-700
//   - active    — bg-blue-800
//   - focus     — 2px ring-blue-500 offset-2
//   - disabled  — opacity-50, cursor-not-allowed, no hover/active shift
//   - loading   — disabled + spinner renders alongside the original
//                 label (not replacing it), so the button width stays
//                 stable when loading flips on/off
//
// API is intentionally narrow — if callers need more variants, add a
// SecondaryButton (outlined blue border) next to this one rather than
// extending this with a `variant` prop. Keeps each component's
// responsibility obvious.

import { forwardRef } from 'react';

function Spinner({ className = 'h-4 w-4' }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block animate-spin rounded-full border-2 border-white/60 border-t-white ${className}`}
    />
  );
}

const PrimaryButton = forwardRef(function PrimaryButton(
  {
    children,
    loading = false,
    disabled = false,
    type = 'button',
    className = '',
    onClick,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2',
        'text-sm font-medium text-white',
        'bg-blue-600 hover:bg-blue-700 active:bg-blue-800',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 disabled:active:bg-blue-600',
        'transition-colors',
        className,
      ].join(' ')}
      {...rest}
    >
      {loading && <Spinner />}
      <span>{children}</span>
    </button>
  );
});

export default PrimaryButton;
